import * as THREE from 'three';
import type { PlayerEntity } from './PlayerEntity';
import { createSprayCan, type SprayCanModel } from './SprayCan';

/**
 * Simple stylized character for third person (and, later, remote players):
 * jacket, beanie, and a spray can in the right hand. Purely procedural —
 * no external assets.
 */
export class CharacterModel {
  readonly group = new THREE.Group();
  private legL: THREE.Mesh;
  private legR: THREE.Mesh;
  private armR: THREE.Group;
  private walkPhase = 0;
  private can: SprayCanModel;

  constructor(jacketColor = 0xc4453b, beanieColor = 0x2e5d8e) {
    const jacket = new THREE.MeshStandardMaterial({ color: jacketColor, roughness: 0.85 });
    const pants = new THREE.MeshStandardMaterial({ color: 0x2b3240, roughness: 0.9 });
    const skin = new THREE.MeshStandardMaterial({ color: 0xe8b89a, roughness: 0.7 });
    const beanieMat = new THREE.MeshStandardMaterial({ color: beanieColor, roughness: 0.95 });

    const makeLeg = (x: number) => {
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.09, 0.52, 4, 10), pants);
      leg.position.set(x, 0.4, 0);
      leg.castShadow = true;
      return leg;
    };
    this.legL = makeLeg(-0.12);
    this.legR = makeLeg(0.12);
    this.group.add(this.legL, this.legR);

    const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.22, 0.42, 6, 14), jacket);
    torso.position.y = 0.98;
    torso.scale.z = 0.82;
    torso.castShadow = true;
    this.group.add(torso);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 18, 14), skin);
    head.position.y = 1.44;
    head.castShadow = true;
    this.group.add(head);

    const beanie = new THREE.Mesh(
      new THREE.SphereGeometry(0.157, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.55),
      beanieMat,
    );
    beanie.position.y = 1.47;
    this.group.add(beanie);
    const pom = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 10, 8),
      new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 1 }),
    );
    pom.position.y = 1.63;
    this.group.add(pom);

    // Left arm, hanging.
    const armL = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.42, 4, 10), jacket);
    armL.position.set(-0.31, 1.0, 0);
    armL.rotation.z = 0.15;
    this.group.add(armL);

    // Right arm group pivots at the shoulder so it can raise while spraying.
    this.armR = new THREE.Group();
    this.armR.position.set(0.31, 1.18, 0);
    const armRMesh = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.42, 4, 10), jacket);
    armRMesh.position.y = -0.24;
    this.armR.add(armRMesh);
    this.can = createSprayCan(0.9);
    this.can.group.position.set(0.02, -0.52, -0.04);
    this.armR.add(this.can.group);
    this.group.add(this.armR);
  }

  setColor(color: string): void {
    this.can.setColor(color);
  }

  nozzleWorldPos(target: THREE.Vector3): THREE.Vector3 {
    return this.can.nozzleWorldPos(target);
  }

  /** Sync visuals from an abstract player entity — local or (later) remote. */
  update(player: PlayerEntity, dt: number): void {
    this.group.position.copy(player.position);
    this.group.rotation.y = player.yaw;

    this.walkPhase += dt * Math.max(player.speed, 0) * 2.4;
    const swing = player.speed > 0.05 ? Math.sin(this.walkPhase) * 0.5 : 0;
    this.legL.rotation.x = swing;
    this.legR.rotation.x = -swing;

    // Raise the can toward the aim while spraying; relax otherwise.
    const targetArm = player.isSpraying ? -1.35 - player.pitch * 0.6 : -0.1;
    this.armR.rotation.x = THREE.MathUtils.lerp(this.armR.rotation.x, targetArm, dt * 10);
  }
}
